import React, { useState } from 'react';
import PropTypes from 'prop-types';
import toast, { Toaster } from 'react-hot-toast';
import KruozAPI from '../../../KruozAPI';
import { useNavigate } from 'react-router-dom';

const PreviewSubmitStep = ({ formData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const navigate = useNavigate();

  const renderWasteType = () => {
    const wasteTypes = {
      general: { label: 'General Waste', image: '/waste-types/general-waste.jpg' },
      green: { label: 'Green Waste', image: '/waste-types/green-waste.jpg' },
      construction: { label: 'Construction Waste', image: '/waste-types/construction-waste.jpg' },
    };
    const selectedWaste = wasteTypes[formData.wasteType];
    return selectedWaste ? (
      <div className="flex items-center bg-gray-100 p-2 rounded">
        <img src={selectedWaste.image} alt={selectedWaste.label} className="w-20 h-20 object-cover rounded mr-4" />
        <span className="text-lg font-semibold">{selectedWaste.label}</span>
      </div>
    ) : formData.wasteType;
  };

  const renderBinSize = () => {
    const binSizes = {
      small: { label: 'Small', image: '/bin-sizes/small.jpg' },
      medium: { label: 'Medium', image: '/bin-sizes/medium.jpg' },
      large: { label: 'Large', image: '/bin-sizes/large.jpg' },
    };
    const selectedBin = binSizes[formData.binSize];
    return selectedBin ? (
      <div className="flex items-center bg-gray-100 p-2 rounded">
        <img src={selectedBin.image} alt={selectedBin.label} className="w-20 h-20 object-cover rounded mr-4" />
        <span className="text-lg font-semibold">{selectedBin.label}</span>
      </div>
    ) : formData.binSize;
  };

  const SectionCard = ({ title, children }) => (
    <div className="bg-white shadow-md rounded-lg p-6 mb-6">
      <h3 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-200">{title}</h3>
      {children}
    </div>
  );

  const InfoRow = ({ label, value }) => (
    <div className="flex flex-wrap mb-2">
      <span className="w-full sm:w-1/3 font-medium text-gray-600">{label}:</span>
      <span className="w-full sm:w-2/3">{value || 'N/A'}</span>
    </div>
  );

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const toastId = toast.loading("Submitting your bin request...");
    
    try {
      const response = await KruozAPI.post('/Quotation/BinRequest', formData);
      
      if (response.status === 200) {
        toast.success("Bin request submitted successfully!", { id: toastId });
        navigate('/customer-home');
        // You can add additional logic here, such as redirecting to a confirmation page
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting bin request:', error);
      toast.error("Failed to submit bin request. Please try again.", { id: toastId });
    } finally {
      setIsSubmitting(false);
      setShowConfirmDialog(false);
    }
  };
  const ConfirmationDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Confirm Submission</h2>
        <p className="mb-6">
          Are you sure you want to submit this bin request? You will not be able to change the request data after this submission.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setShowConfirmDialog(false)}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      
      <SectionCard title="Service Location">
        <InfoRow label="Address" value={formData.streetAddress} />
        <InfoRow label="City" value={formData.city} />
        <InfoRow label="Postal Code" value={formData.postalCode} />
      </SectionCard>

      <SectionCard title="Waste Information">
        <InfoRow label="Waste Type" value={renderWasteType()} />
        <InfoRow label="Hazardous Materials" value={formData.isHazardous ? 'Yes' : 'No'} />
        {formData.isHazardous && (
          <InfoRow label="Hazardous Materials Description" value={formData.hazardousMaterials} />
        )}
      </SectionCard>

      <SectionCard title="Bin Requirements">
        <InfoRow label="Bin Size" value={renderBinSize()} />
      </SectionCard>

      <SectionCard title="Delivery & Pickup">
        <InfoRow label="Rental Start Date" value={formData.rentalStartDate} />
        <InfoRow label="Rental Duration" value={`${formData.rentalDuration} days`} />
        <InfoRow label="Rental End Date" value={formData.rentalEndDate} />
        <InfoRow label="Delivery Instructions" value={formData.deliveryInstructions} />
      </SectionCard>

      <SectionCard title="Contact Information">
        <InfoRow label="Full Name" value={formData.fullName} />
        <InfoRow label="Email" value={formData.email} />
        <InfoRow label="Mobile Number" value={formData.mobileNumber} />
        <InfoRow label="Home Number" value={formData.homeNumber} />
        <InfoRow label="Business Name" value={formData.businessName} />
        {formData.secondContactName && (
          <>
            <div className="mt-4 mb-2 font-semibold text-gray-700">Second Contact</div>
            <InfoRow label="Name" value={formData.secondContactName} />
            <InfoRow label="Mobile" value={formData.secondContactMobile} />
            <InfoRow label="Email" value={formData.secondContactEmail} />
          </>
        )}
      </SectionCard>

      {/* Submit button */}
      <div className="flex justify-center mt-8">
        <button
          onClick={() => setShowConfirmDialog(true)}
          disabled={isSubmitting}
          className={`bg-green-500 text-white px-6 py-3 rounded-lg text-lg font-semibold
                      hover:bg-green-600 transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Bin Request'}
        </button>
      </div>

      {showConfirmDialog && <ConfirmationDialog />}
    </div>
  );
};

PreviewSubmitStep.propTypes = {
  formData: PropTypes.object.isRequired,
};

export default PreviewSubmitStep;